import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useMe, useOrg } from "../lib/session";
import { ago, contactName } from "../lib/format";
import type { Activity, Appointment, Dashboard as DashboardData, Task, Thread } from "../lib/types";
import { Collapsible, countLabel, Empty, ErrorText, Loading, PageHeader } from "../components/ui";
import { useEntitlements } from "../lib/plans";
import { useWords } from "../lib/workspace";
import { ForecastStats } from "./deals";
import { registerWarmer } from "../lib/smooth";
import { addDays, dayKey, fmtTime, startOf } from "../lib/tz";

// Today: what needs doing now. Conversations waiting for a person, today's
// appointments, your tasks and the leads worth a call, then the numbers.

const dashboardQuery = (orgId: string, base: string) => ({ queryKey: [orgId, "dashboard"], queryFn: () => api<DashboardData>(`${base}/dashboard`) });
const activityQuery = (orgId: string, base: string) => ({ queryKey: [orgId, "activities"], queryFn: () => api<{ activities: Activity[] }>(`${base}/activities?limit=100`) });
registerWarmer("", (qc, { orgId, base }) => { qc.prefetchQuery({ ...dashboardQuery(orgId, base), staleTime: 10_000 }); qc.prefetchQuery({ ...activityQuery(orgId, base), staleTime: 10_000 }); });

const TZ = "Asia/Bahrain";
const actorLabel: Record<string, string> = { automation: "Automation", agent: "AI agent", system: "SVENEX", contact: "Customer", member: "Team" };

export default function Dashboard() {
  const { base, orgId } = useOrg();
  const me = useMe();
  const plan = useEntitlements();
  const w = useWords();
  const summary = useQuery({ ...dashboardQuery(orgId, base), staleTime: 0, refetchInterval: 60_000 });
  const activity = useQuery({ ...activityQuery(orgId, base), staleTime: 0 });
  if (summary.isLoading) return <Loading rows={6} />;
  if (summary.error) return <ErrorText error={summary.error} />;
  const d = summary.data!;
  const acts = activity.data?.activities ?? [];
  const first = me.data?.user.display_name.split(" ")[0];
  const hour = Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hourCycle: "h23", timeZone: TZ }).format(new Date()));
  const hello = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const bookings = plan.enabled("bookings");
  return (
    <>
      <PageHeader title={`${hello}${first ? `, ${first}` : ""}`} actions={<span className="muted small">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: TZ })}</span>} />
      <section className="stats today-stats">
        <Stat label="Waiting for a person" value={d.conversations_awaiting_human} to="inbox" urgent={d.conversations_awaiting_human > 0} />
        <Stat label="Overdue tasks" value={d.overdue_tasks} to="tasks" urgent={d.overdue_tasks > 0} />
        <Stat label="Tasks due today" value={d.due_today} to="tasks" />
        <Stat label={`New ${w.lower("contact", true)} this week`} value={d.new_leads_7d} to="contacts" />
      </section>

      <SuggestedAnswers />
      <div className="today-grid">
        <Waiting />
        {bookings && <TodaysAppointments />}
        <MyTasks />
        <section className="card">
          <h2 className="card-title">Leads to follow up {d.hot_leads.length > 0 && <span className="count">{countLabel(d.hot_leads.length)}</span>}</h2>
          {d.hot_leads.length === 0 ? (
            <Empty>No high-intent leads waiting.</Empty>
          ) : (
            <Collapsible items={d.hot_leads} noun="leads" render={(l) => (
              <li key={l.id} className="row-link">
                <Link to={`/o/${orgId}/contacts/${l.id}`} dir="auto">{l.name || l.email || "Unnamed"}</Link>
                <span className="score" title="Lead score">{l.lead_score}</span>
              </li>
            )} />
          )}
        </section>
      </div>

      {plan.enabled("deals") && (
        <>
          <h2 className="section-title">Pipeline</h2>
          <ForecastStats to={`/o/${orgId}/deals`} />
        </>
      )}

      <section className="card">
        <h2 className="card-title">Recent activity</h2>
        {activity.isLoading ? <Loading /> : acts.length === 0 ? <Empty>Nothing yet.</Empty> : (
          <Collapsible items={acts} className="timeline" render={(a) => (
            <li key={a.id}>
              <span dir="auto">{a.summary}</span>
              <small className="muted">
                {a.actor_name ?? actorLabel[a.actor_type] ?? a.actor_type} · {ago(a.occurred_at)}
                {a.contact_id && <> · <Link to={`/o/${orgId}/contacts/${a.contact_id}`}>Open contact</Link></>}
              </small>
            </li>
          )} />
        )}
      </section>
      {plan.enabled("website_agent") && (
        <p className="muted small">
          AI agent, last 7 days: {d.agent_replies_7d.toLocaleString()} replies{d.agent_failures_7d > 0 && `, ${d.agent_failures_7d} failed`}
          {d.first_token_p50_ms != null && `, starts answering in ${(d.first_token_p50_ms / 1000).toFixed(1)}s typically`}.
        </p>
      )}
    </>
  );
}

function Stat({ label, value, to, urgent }: { label: string; value: number; to: string; urgent?: boolean }) {
  return (
    <Link to={to} className={`stat${urgent ? " urgent" : ""}`}>
      <b>{countLabel(value)}</b>
      <span>{label}</span>
    </Link>
  );
}

/** Waiting: conversations a person needs to answer. */
function Waiting() {
  const { base, orgId } = useOrg();
  const q = useQuery({ queryKey: [orgId, "threads", "waiting"], queryFn: () => api<{ threads: Thread[] }>(`${base}/threads?waiting=1`), refetchInterval: 30_000 });
  const list = (q.data?.threads ?? []).filter((t) => t.status !== "closed");
  return (
    <section className="card">
      <h2 className="card-title">Waiting for a person {list.length > 0 && <span className="count urgent">{list.length}</span>}</h2>
      {q.isLoading ? <Loading /> : list.length === 0 ? <Empty>No one is waiting. Your AI agent has it.</Empty> : (
        <ul className="today-list">
          {list.slice(0, 5).map((t) => (
            <li key={t.id}>
              <Link to={`/o/${orgId}/inbox/${t.id}`}>
                <b dir="auto">{t.contact_name || t.contact_email || t.email_from || (t.channel === "whatsapp" ? "WhatsApp customer" : "Website visitor")}</b>
                <span className="muted small" dir="auto">{t.last_message_preview}</span>
              </Link>
              <span className="muted small nowrap">{ago(t.last_message_at)}</span>
            </li>
          ))}
        </ul>
      )}
      {list.length > 5 && <Link className="button-link secondary small" to={`/o/${orgId}/inbox`}>All {list.length} in the inbox</Link>}
    </section>
  );
}

/** TodaysAppointments: who's coming in today. */
function TodaysAppointments() {
  const { base, orgId } = useOrg();
  const w = useWords();
  const today = dayKey(new Date(), TZ);
  const from = startOf(today, TZ);
  const to = startOf(addDays(today, 1), TZ);
  const q = useQuery({
    queryKey: [orgId, "appointments", "today", today],
    queryFn: () => api<{ appointments: Appointment[] }>(`${base}/appointments?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
    refetchInterval: 60_000,
  });
  const list = (q.data?.appointments ?? []).filter((a) => a.status !== "cancelled").sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  return (
    <section className="card">
      <h2 className="card-title">Today's {w.lower("appointment", true)} {list.length > 0 && <span className="count">{list.length}</span>}</h2>
      {q.isLoading ? <Loading /> : list.length === 0 ? <Empty>Nothing booked today.</Empty> : (
        <ul className="today-list">
          {list.slice(0, 6).map((a) => {
            const past = new Date(a.ends_at) < new Date();
            return (
              <li key={a.id} className={past ? "past" : undefined}>
                <span className="time">{fmtTime(new Date(a.starts_at), TZ)}</span>
                <span className="grow">
                  {a.contact_id ? <Link to={`/o/${orgId}/contacts/${a.contact_id}`} dir="auto">{a.contact_name ?? "Customer"}</Link> : <b>{a.contact_name ?? "Customer"}</b>}
                  <span className="muted small"> · {a.service_name} · {a.resource_name}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Link className="button-link secondary small" to={`/o/${orgId}/calendar`}>Open calendar</Link>
    </section>
  );
}

/** MyTasks: your open tasks, overdue and due soonest first, ticked off here. */
function MyTasks() {
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "tasks", "open", "me"];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ tasks: Task[] }>(`${base}/tasks?status=open&assigned=me`) });
  const done = useMutation({
    mutationFn: (id: string) => api(`${base}/tasks/${id}`, { method: "PATCH", json: { status: "done" } }),
    onSettled: () => { qc.invalidateQueries({ queryKey: [orgId, "tasks"] }); qc.invalidateQueries({ queryKey: [orgId, "dashboard"] }); },
  });
  const now = Date.now();
  const list = [...(q.data?.tasks ?? [])].sort((a, b) => (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999"));
  return (
    <section className="card">
      <h2 className="card-title">Your tasks {list.length > 0 && <span className="count">{list.length}</span>}</h2>
      <ErrorText error={done.error} />
      {q.isLoading ? <Loading /> : list.length === 0 ? <Empty>Nothing on your list.</Empty> : (
        <ul className="task-list">
          {list.slice(0, 6).map((t) => {
            const overdue = t.due_at && new Date(t.due_at).getTime() < now;
            return (
              <li key={t.id}>
                <input type="checkbox" aria-label={`Done: ${t.title}`} disabled={allows("readonly") || done.isPending} onChange={() => done.mutate(t.id)} />
                <span className="grow" dir="auto">{t.title}{t.contact_id && <> · <Link to={`/o/${orgId}/contacts/${t.contact_id}`}>{t.contact_name ?? contactName({})}</Link></>}</span>
                {t.due_at && <span className={`small nowrap${overdue ? " overdue" : " muted"}`}>{overdue ? "Overdue" : ago(t.due_at)}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <Link className="button-link secondary small" to={`/o/${orgId}/tasks`}>All tasks</Link>
    </section>
  );
}

/** SuggestedAnswers: questions customers keep asking that could be answered
 *  without the AI. Saving them makes replies instant and cheaper. */
function SuggestedAnswers() {
  const { base, orgId, allows } = useOrg();
  const plan = useEntitlements();
  const on = allows("knowledge") && plan.enabled("saved_answers");
  const q = useQuery({ queryKey: [orgId, "saved-answer-suggestions"], queryFn: () => api<{ suggestions: { id: string }[] }>(`${base}/saved-answers/suggestions`), enabled: on, staleTime: 5 * 60_000 });
  const n = q.data?.suggestions.length ?? 0;
  if (!on || n === 0) return null;
  return (
    <section className="card inline-card suggest-card">
      <div>
        <b>{n} {n === 1 ? "question customers keep asking" : "questions customers keep asking"}</b>
        <p className="muted small">Save an answer once and SVENEX sends it straight away next time, without asking the AI.</p>
      </div>
      <Link className="button-link" to={`/o/${orgId}/knowledge#saved-answers`}>Review {n === 1 ? "it" : "them"}</Link>
    </section>
  );
}
